'use client';

import { Newspaper } from 'lucide-react';
import { useMemo } from 'react';

import { clsx } from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { BlogPostType, BlogTagType } from '@screens/Blog/model/types/blog';
import { ACCENT } from '@shared/constants/colors';
import { ROUTES } from '@shared/constants/routes';
import { BreadcrumbItemType } from '@shared/types/global';
import { Breadcrumbs } from '@shared/ui/Breadcrumbs';
import { Container } from '@shared/ui/Container';
// import { DefaultSelect } from '@shared/ui/DefaultSelect';
import { Pagination } from '@shared/ui/Pagination';

import { formatBlogPostDate, getBlogPostDateValue, getBlogPostImageUrl, getBlogPostUrl } from '../lib/blogPostHelpers';
import { getBlogPostTags } from '../lib/getBlogPostFirstTag';
import cls from './Blog.module.scss';

type BlogProps = {
  posts: BlogPostType[];
  tags: BlogTagType[];
  activeTag?: string;
  activePeriod?: string;
  activePage?: number;
};

const PAGE_SIZE = 8;

const breadcrumbs: BreadcrumbItemType[] = [
  { label: 'Главная', url: ROUTES.MAIN },
  { label: 'Полезные статьи', url: '' }
];

// const periodOptions = [
//   { value: 'all', label: 'За все время' },
//   { value: 'month', label: 'За месяц' },
//   { value: 'year', label: 'За год' }
// ];

const isPostInPeriod = (post: BlogPostType, period: string) => {
  if (period === 'all') {
    return true;
  }

  const dateValue = getBlogPostDateValue(post);

  if (!dateValue) {
    return false;
  }

  const postDate = new Date(dateValue);
  const now = new Date();
  const borderDate = new Date(now);

  if (period === 'month') {
    borderDate.setMonth(now.getMonth() - 1);
  }

  if (period === 'year') {
    borderDate.setFullYear(now.getFullYear() - 1);
  }

  return postDate >= borderDate;
};

export const Blog = ({ posts, tags, activeTag = 'all', activePeriod = 'all', activePage = 1 }: BlogProps) => {
  const router = useRouter();

  const filteredPosts = useMemo(
    () => posts.filter((post) => post.id && post.title && isPostInPeriod(post, activePeriod)),
    [activePeriod, posts]
  );
  const pageAll = Math.max(1, Math.ceil(filteredPosts.length / PAGE_SIZE));
  const page = Math.min(Math.max(activePage, 1), pageAll);
  const pagePosts = filteredPosts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // const selectOptions: SelectOptionType[] = periodOptions.map((option) => ({
  //   ...option,
  //   active: option.value === activePeriod
  // }));

  const pushParams = (params: { tag?: string; period?: string; page?: number }) => {
    const searchParams = new URLSearchParams();
    const tag = params.tag ?? activeTag;
    const period = params.period ?? activePeriod;
    const nextPage = params.page ?? page;

    if (tag !== 'all') {
      searchParams.set('tag', tag);
    }

    if (period !== 'all') {
      searchParams.set('period', period);
    }

    if (nextPage > 1) {
      searchParams.set('page', String(nextPage));
    }

    const query = searchParams.toString();
    router.push(query ? `${ROUTES.BLOG}?${query}` : ROUTES.BLOG);
  };

  return (
    <Container>
      <Breadcrumbs
        className={cls.breadcrumbs}
        data={breadcrumbs}
      />
      <h1 className={cls.title}>Полезные статьи</h1>

      <div className={cls.controls}>
        <div className={cls.tags}>
          <button
            className={clsx(cls.tag, { [cls.activeTag]: activeTag === 'all' })}
            type="button"
            onClick={() => pushParams({ tag: 'all', page: 1 })}
          >
            Все
          </button>
          {tags.map((tag) => (
            <button
              key={tag.id}
              className={clsx(cls.tag, { [cls.activeTag]: activeTag === tag.slug })}
              type="button"
              onClick={() => pushParams({ tag: tag.slug, page: 1 })}
            >
              {tag.name}
            </button>
          ))}
        </div>

        {/*<DefaultSelect*/}
        {/*  className={cls.periodSelect}*/}
        {/*  options={selectOptions}*/}
        {/*  onChange={(option) => pushParams({ period: option.value, page: 1 })}*/}
        {/*/>*/}
      </div>

      {pagePosts.length ? (
        <>
          <div className={cls.grid}>
            {pagePosts.map((post) => {
              const postTags = getBlogPostTags(post);
              const imageUrl = getBlogPostImageUrl(post.previewImage);
              const date = formatBlogPostDate(post);

              return (
                <Link
                  href={getBlogPostUrl(post)}
                  className={cls.card}
                  key={post.id}
                >
                  <div className={cls.imageWrap}>
                    {imageUrl && (
                      <Image
                        className={cls.image}
                        src={imageUrl}
                        alt={post.previewImageAlt || post.title}
                        fill
                        sizes="(max-width: 767px) 100vw, (max-width: 1023px) 50vw, 25vw"
                        unoptimized
                      />
                    )}
                    {postTags.length > 0 && (
                      <div className={cls.cardTags}>
                        {postTags.map((tag, tagIndex) => (
                          <span
                            className={cls.cardTag}
                            key={`${tag.id || tag.slug || tag.name}-${tagIndex}`}
                          >
                            {tag.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <h2 className={cls.cardTitle}>{post.title}</h2>
                  {date && <time className={cls.date}>{date}</time>}
                </Link>
              );
            })}
          </div>

          {pageAll > 1 && (
            <Pagination
              className={cls.pagination}
              data={{
                skip: (page - 1) * PAGE_SIZE,
                limit: PAGE_SIZE,
                page,
                pageAll,
                total: filteredPosts.length
              }}
              changePageNumber={(page) => pushParams({ page })}
            />
          )}
        </>
      ) : (
        <div className={cls.empty}>
          <div className={cls.emptyIcon}>
            <Newspaper
              color={ACCENT}
              size={80}
            />
          </div>
          <h2 className={cls.emptyTitle}>Статьи скоро появятся</h2>
          <div className={cls.emptyDescription}>Мы готовим полезные материалы и скоро добавим их в раздел</div>
        </div>
      )}
    </Container>
  );
};
